// NPM/System Imports
import React from "react";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import Helmet from "react-helmet";
import { graphql, Link } from "gatsby";
import TransitionLink from "gatsby-plugin-transition-link";
import AniLink from "gatsby-plugin-transition-link/AniLink";

// Global Var Imports
import dimensions from "../styles/dimensions";
import colors from "../styles/colors";

// Component Imports
import Hero from "../components/home/Hero"
import About from "../components/home/About";
import Layout from "../components/Layout";
import ProjectCard from "../components/ProjectCard";

const Section = styled("section")`
    margin-bottom: 10em;
    display: flex;
    flex-direction: column;

    @media(max-width:${dimensions.maxwidthTablet}px) {
        margin-bottom: 4em;
    }

    &:last-of-type {
        margin-bottom: 0;
    }
`

const RenderBody = ({ home, projects, meta }) => (
  <>
    <Helmet
        title={meta.title}
        titleTemplate={`%s | ${meta.title}`}
        meta={[
            {
                name: `description`,
                content: meta.description,
            },
            {
                property: `og:title`,
                content: meta.title,
            },
            {
                property: `og:description`,
                content: meta.description,
            },
            {
                property: `og:type`,
                content: `website`,
            },
            {
                name: `twitter:card`,
                content: `summary`,
            },
            {
                name: `twitter:creator`,
                content: meta.author,
            },
            {
                name: `twitter:title`,
                content: meta.title,
            },
            {
                name: `twitter:description`,
                content: meta.description,
            },
        ].concat(meta)}
    />

    <Hero
      headline={home.hero_headline}
      copy={home.hero_text}
      image={home.hero_image}
      cta_text={home.hero_button_text}
      cta_target={home.hero_button_link.url}
    />

    <About
        headline={home.about_headline}
        copy={home.about_body_text}
        image={home.about_accent_image}
        cta_text={home.about_page_link_button_text}
        cta_target={home.about_page_link.url}
    />

    <Section>
      {projects.map((project, i) => (
        <ProjectCard 
          key={i}
          title={project.node.title}
          subtitle={project.node.subtitle}
          description={project.node.preview_description}
          thumbnail={project.node.thumbnail}
          uid={project.node._meta.uid}
        />
      ))}
    </Section>
      
      
  </>
);

export default ({ data }) => {
    //Required check for no data being returned
    const doc = data.prismic.allHomepages.edges.slice(0, 1).pop();
    const projects = data.prismic.allProjects.edges;
    const meta = data.site.siteMetadata;

    if (!doc || !projects) return null;

    return (
        <Layout>
            <RenderBody home={doc.node} projects={projects} meta={meta}/>
        </Layout>
    )
}

RenderBody.propTypes = {
    home: PropTypes.object.isRequired,
    projects: PropTypes.array.isRequired,
    meta: PropTypes.object.isRequired,
};

export const query = graphql`
{
  prismic {
    allHomepages {
      edges {
        node {
          hero_headline
          hero_text
          hero_image
          hero_button_text
          hero_button_link {
            ... on PRISMIC__ExternalLink {
              _linkType
              url
            }
          }
          about_headline
          about_body_text
          about_accent_image
          about_page_link_button_text
          about_page_link {
            ... on PRISMIC__ExternalLink {
              _linkType
              url
            }
          }
          work_title
          work_copy
          work_image
        }
      }
    }
    allProjects {
      edges {
        node {
          title
          subtitle
          preview_description
          thumbnail
          _meta {
            uid
          }
        }
      }
    }
  }
  site {
      siteMetadata {
          title
          description
          author
      }
  }
}
`